import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaWhatsapp, 
  FaClock,
  FaArrowRight,
  FaCheckCircle,
  FaBuilding,
  FaHeadset,
  FaClock as FaClockIcon
} from 'react-icons/fa';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      console.log('Form submitted:', formData);
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
      
      setTimeout(() => setIsSubmitted(false), 5000);
    }, 1500);
  };

  // WhatsApp click handler
  const handleWhatsAppClick = (phoneNumber) => {
    const formattedNumber = phoneNumber.replace(/\s/g, '');
    window.open(`https://wa.me/${formattedNumber}`, '_blank');
  };

  return (
    <section className="py-16 md:py-20 bg-white min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* ─── Header ─── */}
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-blue-100">
              Get in Touch
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight">
              Contact <span className="font-semibold text-blue-600">Us</span>
            </h1>
            <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto text-sm">
              We'd love to hear from you. Get in touch with our team today.
            </p>
          </motion.div>
        </div>

        {/* ─── Contact Info Cards ─── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-14">
          {[
            { icon: FaMapMarkerAlt, label: 'Visit Us', value: 'Nairobi, Kenya' },
            { icon: FaPhone, label: 'Call Us', value: '+254 703 802 020', isPhone: true },
            { icon: FaEnvelope, label: 'Email Us', value: 'info@micropoint.co.ke', isEmail: true },
            { icon: FaWhatsapp, label: 'WhatsApp', value: '+254 730 320 320', isWhatsApp: true },
          ].map((item, index) => {
            const Icon = item.icon;
            
            // Handle click based on contact type
            const handleCardClick = () => {
              if (item.isWhatsApp) {
                handleWhatsAppClick(item.value);
              } else if (item.isPhone) {
                window.location.href = `tel:${item.value.replace(/\s/g, '')}`;
              } else if (item.isEmail) {
                window.location.href = `mailto:${item.value}`;
              }
            };

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                onClick={handleCardClick}
                className={`bg-white rounded-xl p-5 text-center border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all group ${
                  item.isWhatsApp || item.isPhone || item.isEmail ? 'cursor-pointer' : ''
                }`}
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-2.5 group-hover:scale-110 group-hover:bg-blue-100 transition-all">
                  <Icon className="text-lg text-blue-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-800">{item.label}</h4>
                <p className="text-sm text-slate-500 mt-0.5">{item.value}</p>
              </motion.div>
            );
          })}
        </div>

        {/* ─── Contact Form & Sidebar ─── */}
        <div className="grid lg:grid-cols-5 gap-8 md:gap-10">
          
          {/* ─── Form ─── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
              
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4 border border-green-100">
                    <FaCheckCircle className="text-3xl text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Thank You!</h3>
                  <p className="text-slate-500 mt-1.5 text-sm">Your message has been sent successfully. We'll get back to you within 24 hours.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white text-sm placeholder:text-slate-400"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white text-sm placeholder:text-slate-400"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white text-sm placeholder:text-slate-400"
                        placeholder="+254 700 000 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white text-sm placeholder:text-slate-400"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white text-sm placeholder:text-slate-400"
                      placeholder="How can we help?"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white resize-none text-sm placeholder:text-slate-400"
                      placeholder="Tell us about your project..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-md"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message <FaArrowRight className="text-xs" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* ─── Sidebar ─── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2 space-y-5"
          >
            {/* Office Location */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all">
              <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FaMapMarkerAlt className="text-blue-600 text-sm" />
                </div>
                Office Location
              </h3>
              <div className="aspect-video bg-slate-50 rounded-lg mb-4 flex items-center justify-center border border-slate-100">
                <div className="text-center">
                  <FaBuilding className="text-3xl text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-700 font-medium text-sm">Nairobi, Kenya</p>
                  <p className="text-xs text-slate-400">Repen Complex, Mombasa Road</p>
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-all">
                  <FaMapMarkerAlt className="text-blue-500 text-sm" />
                  <span className="text-slate-600">Repen Complex, Mombasa Road</span>
                </div>
                <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-all">
                  <FaClockIcon className="text-blue-500 text-sm" />
                  <span className="text-slate-600">Mon - Fri, 8:00 AM - 6:00 PM</span>
                </div>
                <div 
                  onClick={() => window.location.href = 'tel:+254703802020'}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-all cursor-pointer"
                >
                  <FaPhone className="text-blue-500 text-sm" />
                  <span className="text-slate-600">+254 703 802 020</span>
                </div>
              </div>
            </div>

            {/* Quick Connect */}
            <div className="relative overflow-hidden rounded-2xl p-6 text-white">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <h3 className="text-base font-bold mb-2 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                    <FaWhatsapp className="text-green-400 text-sm" />
                  </div>
                  Quick Connect
                </h3>
                <p className="text-white/60 text-sm mb-4">
                  Chat with us directly on WhatsApp for immediate assistance.
                </p>
                <button
                  onClick={() => handleWhatsAppClick('+254703802020')}
                  className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-lg font-semibold transition-all text-sm w-full hover:shadow-lg hover:shadow-green-500/20"
                >
                  <FaWhatsapp className="text-base" /> Chat on WhatsApp
                </button>
              </div>
            </div>

            {/* Support Info */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all">
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FaHeadset className="text-blue-600 text-sm" />
                </div>
                24/7 Support
              </h4>
              <p className="text-sm text-slate-500 leading-relaxed">
                Our technical support team is available around the clock to assist you.
              </p>
              <div 
                onClick={() => window.location.href = 'tel:+254703802020'}
                className="mt-3 flex items-center gap-3 text-sm text-slate-600 p-2 rounded-lg hover:bg-slate-50 transition-all cursor-pointer"
              >
                <FaPhone className="text-blue-500 text-sm" />
                <span>+254 703 802 020</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}